import { FilterGroupBadges } from '@/app/common/filters/surfaces/badges/FilterGroupBadges.tsx';
import React from 'react';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import type { HandleFilterChanged, HandleFiltersReset } from '@/app/common/filters/core/filterComponentType.ts';
import type { FiltersGroup, FiltersGroups } from '@/app/common/filters/core/filtersTypes.ts';
import { ResetPosition } from '@/app/components/search/components/NoResults/BadgesContainer/types.ts';
import { ResetButton } from '@/app/components/search/components/NoResults/BadgesContainer/styled.ts';
import styled, { css } from 'styled-components';
import type { CustomFilterLabelCreators } from '@/app/common/filters/surfaces/forms/FilterGroupsComponents.tsx';
import { isFilterSelected } from '@/app/common/filters/core/filterSelectedUtils.ts';
import type { RESULT_PAGE_FILTER_RESET_POSITION } from '@/app/common/starTrack/events/resultPageFilterReset.ts';

type FilterGroupsBadgesProps = {
  filtersGroups: FiltersGroups;
  resetPosition: ResetPosition;
  resetTrackingPosition: RESULT_PAGE_FILTER_RESET_POSITION;
  customFilterLabelCreators: CustomFilterLabelCreators;
  isLoading: boolean;
  handleFilterChanged: HandleFilterChanged;
  handleFiltersReset: HandleFiltersReset;
};

const Container = styled.div<{ resetPosition: ResetPosition }>`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  flex-direction: row;
  width: 100%;


  ${({ resetPosition }) =>
    resetPosition === ResetPosition.Right &&
    css`
      justify-content: center;
    `}
}
`;

const ResetFullWidthContainer = styled.div`
  width: 100%;
  text-align: center;

  button {
    margin: 0 auto;
  }
`;

const Reset = ({
  resetPosition,
  onClick,
}: {
  resetPosition: ResetPosition;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => {
  const { t } = useTranslation();

  if (resetPosition === ResetPosition.Left) {
    return <ResetButton onClick={onClick}>{t('vacation.oss.filters.clearall')}</ResetButton>;
  }

  if (resetPosition === ResetPosition.Right) {
    return (
      <ResetFullWidthContainer>
        <ResetButton onClick={onClick}>{t('vacation.oss.filters.clearall')}</ResetButton>
      </ResetFullWidthContainer>
    );
  }

  return null;
};

// Utility to check if any filter in any group is selected (i.e., any badge is active)
export function hasActiveBadges(filtersGroups: FiltersGroups): boolean {
  return filtersGroups.some((group) => {
    if ('filters' in group) return group?.filters?.some(isFilterSelected);
    return false;
  });
}

export const FilterGroupsBadges = ({
  filtersGroups,
  resetPosition,
  resetTrackingPosition,
  customFilterLabelCreators,
  isLoading,
  handleFilterChanged,
  handleFiltersReset,
}: FilterGroupsBadgesProps) => {
  const filtersGroupsComponents = filtersGroups
    .map((filtersGroup) => (
      <FilterGroupBadges
        key={filtersGroup.id}
        filtersGroup={filtersGroup as FiltersGroup}
        customFilterLabelCreators={customFilterLabelCreators}
        isLoading={isLoading}
        handleFilterChanged={handleFilterChanged}
      />
    ))
    .filter((f) => !!f);

  const hasBadges = hasActiveBadges(filtersGroups);
  if (!hasBadges) return null;

  const reset = <Reset resetPosition={resetPosition} onClick={() => handleFiltersReset(resetTrackingPosition)} />;

  return (
    <Container resetPosition={resetPosition}>
      {resetPosition === ResetPosition.Left && reset}
      {filtersGroupsComponents}
      {resetPosition === ResetPosition.Right && reset}
    </Container>
  );
};
